import React, { useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import { CalendarValue } from '../../../components/DateTimePicker';
import { useAuth } from '../../../context/Auth';
import { deacService } from '../../../services/central-api/deac';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import WorkplacesDropdown from '../../../components/deac/WorkplacesDropdown';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import UserDropdown from '../../../components/deac/UserDropdown';
import ActingBodiesCommandDropdown from '../../../components/deac/ActingBodiesCommandDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onClose: () => void;
}

const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

export default function ReportModal({ onClose, isModalVisible, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [name, setName] = useState<string>('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(0);
    const [workplaceId, setWorkplaceId] = useState<number>(0);
    const [userId, setUserId] = useState<number>(0);
    const [startInDateFilter, setStartInDateFilter] = useState<CalendarValue>(null);
    const [startInHourFilter, setStartInHourFilter] = useState<string>('');
    const [endInDateFilter, setEndInDateFilter] = useState<CalendarValue>(null);
    const [endInHourFilter, setEndInHourFilter] = useState<string>('');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(4);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(3);


    function isNameInvalid(value: string) {
        return value == '' || !regex.test(value);
    }

    function isFormValid() {
        if (getActingBodyId() === 0) {
            return false;
        }
        if (isNameInvalid(name)) {
            return false;
        }
        if (!startInDateFilter || !startInHourFilter || !endInDateFilter || !endInHourFilter) {
            return false;
        }
        const startDateTime = moment(`${moment(startInDateFilter).format('DD/MM/YYYY')} ${startInHourFilter}`, 'DD/MM/YYYY HH:mm');
        const endDateTime = moment(`${moment(endInDateFilter).format('DD/MM/YYYY')} ${endInHourFilter}`, 'DD/MM/YYYY HH:mm');
        if (!startDateTime.isBefore(endDateTime)) {
            return false;
        }

        return true;
    }

    function onCloseNewReportModal() {
        setModalVisible(false);
        resetReportForm();
    }

    async function formSubmit() {
        try {
            await deacService.createReport({
                name: name,
                startDate: moment(`${moment(startInDateFilter).format('DD/MM/YYYY')} ${startInHourFilter}`, 'DD/MM/YYYY HH:mm').toISOString(),
                endDate: moment(`${moment(endInDateFilter).format('DD/MM/YYYY')} ${endInHourFilter}`, 'DD/MM/YYYY HH:mm').toISOString(),
                actingBodyId: getActingBodyId(),
                actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
                actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
                workplaceId: workplaceId !== 0 ? workplaceId : undefined,
                userId: userId !== 0 ? userId : undefined,
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            onCloseNewReportModal();
            onClose();
        }
    }

    function resetReportForm() {
        setName('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setWorkplaceId(0);
        setUserId(0);
        setStartInDateFilter(null);
        setStartInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.deac_manage_all_acting_bodies_reports) {
            return actingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Modal transparent={true} visible={isModalVisible} animationType='fade' onRequestClose={() => onCloseNewReportModal()} >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 400 }]}>
                    <ScrollView contentContainerStyle={styles.scrollContent}>
                        <MyAppText style={{ fontSize: 22, fontWeight: 'bold', color: theme.colors.labelColor }}>{translate('vacanciesReport')}</MyAppText>
                        <ActingBodiesDropdown
                            includeAllOption={false}
                            value={getActingBodyId()}
                            setValue={setActingBodyId}
                            zIndex={7}
                            labelStyle={styles.fieldsLabelStyle}
                            disabled={!authenticatedUser?.permissions.deac_manage_all_acting_bodies_reports}
                        />

                        <ActingBodiesCommandDropdown
                            includeAllOption
                            value={actingBodyCommandId}
                            setValue={setActingBodyCommandId}
                            zIndex={6}
                            labelStyle={styles.fieldsLabelStyle}
                            actingBodyId={getActingBodyId()}
                        />

                        <ActingBodiesUnitsDropdown
                            includeAllOption
                            value={actingBodyUnitId}
                            setValue={setActingBodyUnitId}
                            zIndex={5}
                            labelStyle={styles.fieldsLabelStyle}
                            actingBodyId={getActingBodyId()}
                        />

                        <FormInput
                            label={translate('name')}
                            value={name}
                            onChangeText={setName}
                            invalid={isNameInvalid}
                            errorMessage={translate('invalidCharacters')}
                            labelStyle={styles.fieldsLabelStyle}
                        />

                        <View style={{ zIndex: estimatedStartZIndex }}>
                            <FilterDatePicker
                                date={startInDateFilter}
                                onChangeDate={setStartInDateFilter}
                                time={startInHourFilter}
                                onChangeTime={setStartInHourFilter}
                                label={translate('beginIn')}
                                maxDate={new Date()}
                                onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                onCalendarClose={() => setEstimatedStartZIndex(4)}
                                labelStyle={styles.fieldsLabelStyle}
                            />
                        </View>
                        <View style={{ zIndex: estimatedFinishZIndex }}>
                            <FilterDatePicker
                                date={endInDateFilter}
                                onChangeDate={setEndInDateFilter}
                                time={endInHourFilter}
                                onChangeTime={setEndInHourFilter}
                                label={translate('endIn')}
                                maxDate={new Date()}
                                onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                onCalendarClose={() => setEstimatedFinishZIndex(3)}
                                labelStyle={styles.fieldsLabelStyle}
                            />
                        </View>

                        <WorkplacesDropdown
                            setValue={setWorkplaceId}
                            value={workplaceId}
                            actingBodyId={getActingBodyId()}
                            actingBodyUnitId={actingBodyUnitId}
                            actingBodyCommandId={actingBodyCommandId}
                            zIndex={2}
                            labelStyle={styles.fieldsLabelStyle}
                        />

                        <UserDropdown
                            label={translate('agent')}
                            actingBodyId={getActingBodyId()}
                            actingBodyUnitId={actingBodyUnitId}
                            setValue={setUserId}
                            value={userId}
                            zIndex={1}
                            labelStyle={styles.fieldsLabelStyle}
                        />

                    </ScrollView>
                    <FormActions
                        viewStyle={{ justifyContent: 'space-around' }}
                        buttonStyle={{ padding: 9 }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        onSubmit={formSubmit}
                        onClose={() => onCloseNewReportModal()}
                        disabled={!isFormValid()}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 15,
        maxHeight: 690,
        padding: 20
    },
    scrollContent: {
        rowGap: 15,
        padding: 5,
    },
    fieldsLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
